import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Fancybox from "../../helpers/Fancybox";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { FiChevronLeft, FiChevronRight, FiChevronUp, FiChevronDown } from "react-icons/fi";
import ApiService from "../../api/ApiService";

const CHANNEL_ID = "UCMze6fxA4QX8mYtCb0qwsrw";

const YouTubeSection = () => {
  const [channelData, setChannelData] = useState(null);
  const [videos, setVideos] = useState([]);
  const [API_KEY, setApikey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const ref = useRef();
  const reff = useRef();

  useEffect(() => {
    const initializeYouTube = async () => {
      try {
        setLoading(true);
        // First fetch the API key
        const apidata = await ApiService.getGoogleapikey();
        setApikey(apidata.key);
        
        // Then fetch YouTube data
        await fetchYouTubeData(apidata.key);
      } catch (error) {
        console.error("❌ Error initializing YouTube:", error);
        setError("Failed to load YouTube content");
      } finally {
        setLoading(false);
      }
    };

    initializeYouTube();
  }, []);

  const fetchYouTubeData = async (apiKey) => {
    try {
      const [ytChannel, ytVideos] = await Promise.all([
        axios.get(`https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics&id=${CHANNEL_ID}&key=${apiKey}`),
        axios.get(`https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${CHANNEL_ID}&maxResults=100&order=date&type=video&key=${apiKey}`),
      ]);

      const videoIds = ytVideos.data.items
        .map(item => item?.id?.videoId)
        .filter(Boolean);

      if (videoIds.length > 0) {
        const statsRes = await axios.get(`https://www.googleapis.com/youtube/v3/videos`, {
          params: {
            part: 'statistics',
            id: videoIds.join(','),
            key: apiKey,
          },
        });

        const statsMap = statsRes.data.items.reduce((acc, item) => {
          acc[item.id] = item.statistics.viewCount;
          return acc;
        }, {});

        const combinedVideos = ytVideos.data.items.map(item => ({
          ...item,
          viewCount: statsMap[item?.id?.videoId] || '0',
        }));

        setVideos(combinedVideos);
      }

      setChannelData(ytChannel.data?.items?.[0]);
    } catch (error) {
      console.error("❌ Error fetching YouTube data:", error);
      throw error;
    }
  };

  const getDaysAgo = (publishedAt) => {
    const publishedDate = new Date(publishedAt);
    const today = new Date();
    const diffDays = Math.floor((today - publishedDate) / (1000 * 60 * 60 * 24));
    return diffDays === 0 ? "Today" : `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  const formatSubscribers = (count) => {
    count = parseInt(count || 0, 10);
    if (count >= 1_000_000) {
      return (count / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    } else if (count >= 1_000) {
      return (count / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    } else {
      return count.toString();
    }
  };

  const subscribe = () => {
    window.open(`https://www.youtube.com/channel/${CHANNEL_ID}?sub_confirmation=1`, '_blank');
  };

  const visit = () => {
    window.open(`https://www.youtube.com/@ISKCONATTAPUR`, '_blank');
  };

  const viewAllVideos = () => {
    window.open(`https://www.youtube.com/@ISKCONATTAPUR/videos`, '_blank');
  };

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-warning" role="status">
          <span className="visually-hidden">Loading YouTube content...</span>
        </div>
        <p className="mt-3">Loading YouTube videos...</p>
      </div>
    );
  }

  if (error || !channelData) {
    return (
      <div className="container mt-5 text-center">
        <div className="alert alert-warning" role="alert">
          <h4>YouTube Content Unavailable</h4>
          <p>We're having trouble loading YouTube videos right now. Please try again later.</p>
          <button 
            className="btn btn-dark text-white"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container mb-5">
        <h3 className="fw-bold text-dark">LATEST FROM YOUTUBE</h3>
        <p className="text-secondary">
          Subscribe to our <strong>YouTube channel</strong> to watch live aartis, recorded discourses, 
          temple events, and bhajan-kirtan sessions. Experience the divine atmosphere of ISKCON Attapur 
          from anywhere in the world.
        </p>
      </div>
      
      <div className="container youtube-container mb-5">
        {/* Channel Information */}
        <div className="row align-items-center mb-4 bg-lightt p-3 rounded">
          <div className="col-12 col-md-2 text-center mb-3 mb-md-0">
            <img
              src={channelData?.snippet?.thumbnails?.default?.url || "/images/fallback-logo.png"}
              alt="Channel logo"
              className="rounded-circle img-fluid"
              height={117}
              width={117}
            />
          </div>
          <div className="col-12 col-md-7">
            <h2 className="temple-title">{channelData?.snippet?.title}</h2>
            <div className="subscribe-stats">
              @ISKCONATTAPUR &nbsp;&nbsp; {formatSubscribers(channelData?.statistics?.subscriberCount)} subscribers &nbsp;•&nbsp; {" "}
              {parseInt(channelData?.statistics?.videoCount || 0).toLocaleString()} videos
            </div>
            <div className="subscribe-stats">
              <p className="hare-text">Hare Krishna!</p>
              <a href="https://www.iskconattapur.com" target="_blank" rel="noopener noreferrer">
                iskconattapur.com
              </a>&nbsp; and more 4 links
            </div>
          </div>
          <div className="col-12 col-md-3 text-center">
            <div className="row mt-5">
              <div className="col-6">
                <button onClick={subscribe} className="btn btn-dark text-white mb-2 w-100 rounded">
                  Subscribe
                </button>
              </div>
              <div className="col-6">
                <button onClick={visit} className="btn btn-dark text-white mb-2 w-100 rounded">
                  Visit Channel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="border-div"></div>

        {/* Main Video and Sidebar */}
        {videos.length > 0 && (
          <div className="video-wrapper">
            <div className="video-container">
              <div className="video-holder">
                <Fancybox>
                  <a
                    data-fancybox="video"
                    href={`https://www.youtube.com/watch?v=${videos[0]?.id?.videoId}`}
                    data-thumb={videos[0]?.snippet?.thumbnails?.medium?.url}
                    className="video-holder-card"
                    style={{ display: "block", borderRadius: "12px", overflow: "hidden" }}
                  >
                    <div className="card video-holder-card" style={{ border: "none", borderRadius: "0" }}>
                      <img
                        src={videos[0]?.snippet?.thumbnails?.high?.url}
                        alt={videos[0]?.snippet?.title}
                        className="card-img-top"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "0",
                        }}
                        loading="lazy"
                      />
                    </div>
                  </a>
                </Fancybox>
                <div className="title-div mt-2">
                  <h3 className="video-title">{videos[0]?.snippet?.title}</h3>
                </div>
              </div>
            </div>

            <div className="view-details-container">
              <div className="categories-list">
                <div className="category-item">All</div>
                <div className="category-item active">From ISKCON ATTAPUR</div>
                <div className="category-item">Rituals</div>
                <div className="category-item">Related</div>
              </div>

              <Splide
                className="testimonial-slider"
                ref={reff}
                options={{
                  type: 'loop',
                  direction: 'ttb',
                  height: '400px',
                  perPage: 3,
                  perMove: 1,
                  gap: '1rem',
                  arrows: false,
                  pagination: false,
                  autoplay: true,
                  speed: 1000,
                  interval: 8000,
                  breakpoints: {
                    576: { perPage: 3, height: '400px' },
                    768: { perPage: 3, height: '500px' },
                    1024: { perPage: 3, height: '600px' },
                  },
                }}
              >
                {videos.slice(1).map((video, index) => (
                  <SplideSlide key={`${video.id.videoId}-${index}`}>
                    <div className="col-md-12 mb-4">
                      <Fancybox>
                        <a data-fancybox="video" href={`https://www.youtube.com/watch?v=${video.id.videoId}`}>
                          <div className="view-all-card">
                            <div className="thumbnail">
                              <img 
                                src={video.snippet.thumbnails.medium.url} 
                                alt={video.snippet.title} 
                                className="thumbnail-image"
                                loading="lazy"
                              />
                              <div className="overlay">
                                <div className="duration"></div>
                              </div>
                            </div>
                            <div className="view-details">
                              <h3 className="view-title">{video.snippet.title}</h3>
                              <div className="ms-auto">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                                  className="bi bi-three-dots-vertical" viewBox="0 0 16 16">
                                  <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </a>
                      </Fancybox>
                    </div>
                  </SplideSlide>
                ))}
              </Splide>

              <div className="button-center mt-2">
                <button onClick={viewAllVideos} className="btn btn-dark text-white mb-2 rounded-0">
                  View All
                </button>
                <button 
                  className="btn btn-dark text-white mb-2 rounded-0" 
                  onClick={() => reff.current?.splide?.go("-1")}
                >
                  <FiChevronUp />
                </button>
                <button 
                  className="btn btn-dark text-white mb-2 rounded-0" 
                  onClick={() => reff.current?.splide?.go("+1")}
                >
                  <FiChevronDown />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Previous Videos Section */}
        {videos.length > 1 && (
          <div className="past-wrapper row mt-3">
            <h2 className="post-live-stream">Previous Videos</h2>
            <Splide
              className="testimonial-slider"
              ref={ref}
              options={{
                perPage: 4,
                perMove: 1,
                gap: 20,
                arrows: false,
                pagination: false,
                autoplay: true,
                speed: 1000,
                interval: 8000,
                breakpoints: {
                  576: { perPage: 1, gap: 20 },
                  768: { perPage: 2 },
                  992: { perPage: 2 },
                  1024: { perPage: 3 },
                  1200: { perPage: 3 },
                  1400: { perPage: 4 },
                },
              }}
            >
              {videos.slice(1).map((video, index) => (
                <SplideSlide key={`prev-${video.id.videoId}-${index}`}>
                  <div className="past-video-card">
                    <Fancybox>
                      <a data-fancybox="video" href={`https://www.youtube.com/watch?v=${video.id.videoId}`}>
                        <div className="past-video-image">
                          <img 
                            src={video.snippet.thumbnails.medium.url} 
                            alt={video.snippet.title} 
                            className="card-img-top"
                            loading="lazy"
                          />
                        </div>
                        <div className="video-card-body mb-2">
                          <h4 className="video-card-title-text">
                            {video.snippet.title.length > 50
                              ? `${video.snippet.title.substring(0, 50)}...`
                              : video.snippet.title}
                          </h4>
                          <span className="video-date">
                            {formatSubscribers(video.viewCount)} views &nbsp;·&nbsp; {getDaysAgo(video.snippet.publishedAt)}
                          </span>
                        </div>
                      </a>
                    </Fancybox>
                  </div>
                </SplideSlide>
              ))}
            </Splide>
            
            <div className="arrow-wrapp">
              <button
                className="slider-btn prev box-hover"
                onClick={() => ref.current?.splide?.go("-1")}
              >
                <FiChevronLeft />
              </button>
              <button
                className="slider-btn next box-hover"
                onClick={() => ref.current?.splide?.go("+1")}
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default YouTubeSection;